import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../../services/ApiService';

interface DiagnosticsSummary {
    totalEvents: number;
    byLevel: { level: string; count: number }[];
}

interface ToolQualitySummaryProps {
    toolId: number;
    toolCode: string;
    toolName: string;
    openReportCount: number;
    isDebug: boolean;
}

const ToolQualitySummary: React.FC<ToolQualitySummaryProps> = ({
    toolId,
    toolCode,
    toolName,
    openReportCount,
    isDebug,
}) => {
    const [summary, setSummary] = useState<DiagnosticsSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setLoadError(false);
        apiService.adminGetGlobalDiagnostics<DiagnosticsSummary>({
            toolCode,
            sinceHours: 24,
            limit: 1,
        }).then(response => {
            if (active) setSummary(response);
        }).catch(() => {
            if (active) setLoadError(true);
        }).finally(() => {
            if (active) setLoading(false);
        });
        return () => { active = false; };
    }, [toolCode]);

    const counts = useMemo(() => {
        const result: Record<string, number> = { Error: 0, Warning: 0, Info: 0 };
        for (const item of summary?.byLevel ?? []) result[item.level] = item.count;
        return result;
    }, [summary]);

    const query = `toolCode=${encodeURIComponent(toolCode)}`;

    return (
        <div className="d-flex flex-column gap-3">
            <div className="alert alert-light border d-flex flex-wrap align-items-center gap-3 mb-0">
                <div className={`quality-status-icon ${isDebug ? 'debug' : 'standard'}`}>
                    <i className={`bi ${isDebug ? 'bi-bug-fill' : 'bi-shield-check'}`} />
                </div>
                <div className="flex-grow-1">
                    <div className="fw-semibold">{isDebug ? 'Tool in modalità Debug' : 'Raccolta standard attiva'}</div>
                    <div className="small text-body-secondary">
                        Il dettaglio operativo di {toolName} è gestito nel Quality Center per evitare viste duplicate.
                    </div>
                </div>
                <Link to={`/quality/issues?${query}`} className="btn btn-primary btn-sm">
                    Apri triage centralizzato <i className="bi bi-arrow-right ms-1" />
                </Link>
            </div>

            {loadError && (
                <div className="alert alert-warning mb-0 small">
                    Il riepilogo diagnostico non è disponibile; gli eventi restano consultabili nel Quality Center.
                </div>
            )}

            <div className="row g-3">
                <ToolMetric label="Segnalazioni aperte" value={openReportCount} icon="bi-flag" color="primary" loading={false} />
                <ToolMetric label="Errori · 24h" value={counts.Error} icon="bi-x-octagon" color="danger" loading={loading} />
                <ToolMetric label="Warning · 24h" value={counts.Warning} icon="bi-exclamation-triangle" color="warning" loading={loading} />
                <ToolMetric label="Eventi · 24h" value={summary?.totalEvents ?? 0} icon="bi-activity" color="info" loading={loading} />
            </div>

            <div className="card shadow-sm">
                <div className="card-header bg-body-tertiary"><strong>Azioni</strong></div>
                <div className="card-body d-flex flex-wrap gap-2">
                    <Link to={`/quality/issues?${query}`} className="btn btn-outline-primary">
                        <i className="bi bi-exclamation-diamond me-2" />Problemi aggregati
                    </Link>
                    <Link to={`/quality/reports?toolId=${toolId}&${query}`} className="btn btn-outline-primary">
                        <i className="bi bi-flag me-2" />Segnalazioni utenti
                    </Link>
                    <Link to={`/quality/events?${query}`} className="btn btn-outline-primary">
                        <i className="bi bi-activity me-2" />Eventi grezzi
                    </Link>
                </div>
            </div>

            <div className="small text-body-secondary">
                Per modificare la modalità di raccolta usa il campo <strong>Stato</strong> nell’intestazione del tool.
                La cancellazione massiva degli eventi non è più esposta nella scheda del singolo tool.
            </div>
        </div>
    );
};

const ToolMetric: React.FC<{
    label: string;
    value: number;
    icon: string;
    color: string;
    loading: boolean;
}> = ({ label, value, icon, color, loading }) => (
    <div className="col-12 col-sm-6 col-xl-3">
        <div className="card shadow-sm h-100">
            <div className="card-body d-flex justify-content-between align-items-center">
                <div>
                    <div className="small text-body-secondary">{label}</div>
                    <div className={`fs-3 fw-bold text-${color}`}>
                        {loading ? <span className="placeholder col-5" /> : value}
                    </div>
                </div>
                <i className={`bi ${icon} fs-2 text-${color} opacity-25`} />
            </div>
        </div>
    </div>
);

export default ToolQualitySummary;
